import { AlertTriangle } from "lucide-react";
import { Link } from "react-router";
import CleButton from "./CleButton";
import type { AwaitingEvidenceItem } from "../models/awaitingEvidence";
import { toAwaitingEvidenceRow } from "../utils/awaitingEvidence";

type AwaitingEvidenceWarningProps = {
  continueButtonId: string;
  continueLabel: string;
  items: AwaitingEvidenceItem[];
  /** Links væk fra siden mister et uindsendt udvalg, så de slås fra i indsend-dialogen. */
  linkToDocuments?: boolean;
  onContinue: () => void;
  onWait: () => void;
  waitButtonId: string;
  waitLabel: string;
};

/**
 * Advarer om dokumentation, der afventer AI-analysen eller sekretariatet. Den kan ikke
 * vælges i indberetningen, og en indsendt indberetning kan ikke ændres, så advokater, der
 * indberettede alligevel, bad bagefter om at få den talt med.
 *
 * At vente er den anbefalede handling og har derfor den fremhævede knap.
 */
const AwaitingEvidenceWarning = ({
  continueButtonId,
  continueLabel,
  items,
  linkToDocuments = false,
  onContinue,
  onWait,
  waitButtonId,
  waitLabel,
}: AwaitingEvidenceWarningProps) => {
  const rows = items.map(toAwaitingEvidenceRow);

  return (
    <div>
      <div className="d-flex align-items-start gap-3 mb-3">
        <span
          className="rounded-circle bg-warning-subtle text-warning-emphasis d-inline-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: "3rem", height: "3rem" }}
          aria-hidden="true"
        >
          <AlertTriangle size={24} strokeWidth={2} />
        </span>
        <div>
          {/* Ordlyden er sekretariatets (Camilla Overlund, 9. oktober 2026). */}
          <h2 className="display-4 fw-semibold mb-2">
            Vent! Vil du indberette, selvom din dokumentation ikke er behandlet?
          </h2>
          <p className="mb-0">
            Følgende dokumentation er endnu ikke behandlet og vil derfor ikke
            indgå i din indberetning, hvis den foretages nu.
          </p>
        </div>
      </div>

      <ul className="list-unstyled d-flex flex-column gap-2 mb-3">
        {rows.map((row) => {
          const name = row.fileName
            ? `${row.typeLabel}: ${row.fileName}`
            : row.typeLabel;

          return (
            <li
              key={row.id}
              className="d-flex flex-wrap align-items-center justify-content-between gap-2 border rounded-3 px-3 py-2"
            >
              <span className="text-break">
                {linkToDocuments && row.documentPath ? (
                  <Link to={row.documentPath}>{name}</Link>
                ) : (
                  name
                )}
              </span>
              {/* Bootstrap-badges frem for cle-status-badge: modalen ligger i en portal uden
                  for .cle-compliance-feature, hvor CLE's farvetokens ikke er defineret. */}
              <span
                className={`badge rounded-pill ${
                  row.isAwaitingSecretariat
                    ? "text-bg-warning"
                    : "text-bg-secondary"
                }`}
              >
                {row.statusLabel}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="alert alert-warning" role="alert">
        <strong>En indsendt indberetning er endelig.</strong> Dokumentation, der
        godkendes, efter du har indberettet, indgår ikke automatisk i
        indberetningen. Overvej om du vil vente med at indberette, til din
        dokumentation er behandlet.
      </div>

      <div className="d-flex flex-wrap justify-content-between gap-3">
        <CleButton id={continueButtonId} variant="outline" onClick={onContinue}>
          {continueLabel}
        </CleButton>
        <CleButton id={waitButtonId} onClick={onWait}>
          {waitLabel}
        </CleButton>
      </div>
    </div>
  );
};

export default AwaitingEvidenceWarning;
